import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import styled from 'styled-components';

import { getShopRequestTranscriptRequirement, updateShopRequestTranscriptRequirement } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';

import { useEditShopRequestModalContext } from './EditShopRequestModalContext';
import EditShopRequestModalTranscriptRequirementAlert from './EditShopRequestModalTranscriptRequirementAlert';
import EditShopRequestModalTranscriptRequirementResolution from './EditShopRequestModalTranscriptRequirementResolution';
import { TRANSCRIPT_REQUIREMENT_ACTION, TRANSCRIPT_REQUIREMENT_STATUS } from '../constants';
import { ShopRequestTranscriptRequirementAction, ShopRequestTranscriptRequirementType } from '../types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LoadingContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  border: 1px solid var(--error-red-border-light);
  border-radius: 8px;
  background: var(--error-red-background);
  color: var(--error-red-dark);
  font-size: 12px;
  padding: 12px;
`;

const TRANSCRIPT_REQUIREMENT_ACTION_SUCCESS_MESSAGE = {
  [TRANSCRIPT_REQUIREMENT_ACTION.CONFIRM]: 'Transcript requirement confirmed.',
  [TRANSCRIPT_REQUIREMENT_ACTION.REJECT]: 'Transcript requirement rejection saved.',
  [TRANSCRIPT_REQUIREMENT_ACTION.CLEAR]: 'Transcript requirement cleared.',
};

const EditShopRequestModalTranscriptRequired = () => {
  const { shopRequest, refetchShopRequests } = useEditShopRequestModalContext();
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const [showReasonForm, setShowReasonForm] = useState(false);
  const [reason, setReason] = useState('');
  const [activeAction, setActiveAction] = useState<ShopRequestTranscriptRequirementAction | null>(null);
  const [displayedRequirement, setDisplayedRequirement] = useState<ShopRequestTranscriptRequirementType>();
  const [actionBaseRequirement, setActionBaseRequirement] = useState<ShopRequestTranscriptRequirementType>();

  const selectedShopperId = shopRequest?.shopper?.userId ?? null;
  const transcriptRequirementQueryKey = ['shop-request', shopRequest?.id, 'transcript-requirement', selectedShopperId];
  const {
    data: transcriptRequirement,
    isLoading: isTranscriptRequirementLoading,
    isFetching: isTranscriptRequirementFetching,
    refetch: refetchTranscriptRequirement,
  } = useQuery<ShopRequestTranscriptRequirementType>({
    queryKey: transcriptRequirementQueryKey,
    queryFn: () => getShopRequestTranscriptRequirement(shopRequest?.id as number, selectedShopperId),
    enabled: Boolean(shopRequest?.id),
    keepPreviousData: false,
    refetchOnWindowFocus: false,
    onError: () => {
      showMessage('error', 'Failed to fetch transcript requirement.');
    },
  });

  const clearReasonForm = () => {
    setReason('');
    setShowReasonForm(false);
  };

  useEffect(() => {
    setDisplayedRequirement(undefined);
    setActionBaseRequirement(undefined);
    clearReasonForm();
  }, [shopRequest?.id, selectedShopperId]);

  useEffect(() => {
    if (!activeAction && transcriptRequirement) {
      setDisplayedRequirement(transcriptRequirement);
    }
  }, [activeAction, transcriptRequirement]);

  const requirement = activeAction && actionBaseRequirement ? actionBaseRequirement : displayedRequirement;
  const trimmedReason = reason.trim();
  const isLoadingRequirement = isTranscriptRequirementLoading || (isTranscriptRequirementFetching && !requirement);
  const isSubmitting = Boolean(activeAction) || isLoadingRequirement;

  if (isLoadingRequirement) {
    return (
      <Container>
        <LoadingContainer>Loading transcript requirement...</LoadingContainer>
      </Container>
    );
  }

  if (!requirement?.is_required) {
    return null;
  }

  const isResolved =
    requirement.status === TRANSCRIPT_REQUIREMENT_STATUS.CONFIRMED ||
    requirement.status === TRANSCRIPT_REQUIREMENT_STATUS.REJECTED;

  const applyAction = async (action: ShopRequestTranscriptRequirementAction, rejectionReason?: string | null) => {
    if (!shopRequest?.id || activeAction) {
      return;
    }

    if (!selectedShopperId) {
      showMessage('error', 'Select a shopper before updating the transcript requirement.');
      return;
    }

    setActionBaseRequirement(requirement);
    setActiveAction(action);
    try {
      await updateShopRequestTranscriptRequirement(shopRequest.id, {
        action,
        shopper_id: selectedShopperId,
        rejection_reason: rejectionReason,
      });

      await queryClient.invalidateQueries(transcriptRequirementQueryKey);
      const { data: refetchedRequirement } = await refetchTranscriptRequirement();
      await refetchShopRequests();
      if (refetchedRequirement) {
        setDisplayedRequirement(refetchedRequirement);
      }
      clearReasonForm();
      setActiveAction(null);
      setActionBaseRequirement(undefined);
      showMessage('success', TRANSCRIPT_REQUIREMENT_ACTION_SUCCESS_MESSAGE[action]);
    } catch (error) {
      setActiveAction(null);
      setActionBaseRequirement(undefined);
      showMessage('error', 'Failed to update transcript requirement.');
      console.error('Failed to update transcript requirement.', error);
    }
  };

  const submitReason = () => {
    if (!trimmedReason) {
      return;
    }

    applyAction(TRANSCRIPT_REQUIREMENT_ACTION.REJECT, trimmedReason);
  };

  return (
    <Container>
      <EditShopRequestModalTranscriptRequirementAlert
        requirement={requirement}
        activeAction={activeAction}
        isResolved={isResolved}
        isSubmitting={isSubmitting}
        showReasonForm={showReasonForm}
        reason={reason}
        trimmedReason={trimmedReason}
        onConfirm={() => applyAction(TRANSCRIPT_REQUIREMENT_ACTION.CONFIRM)}
        onShowReasonForm={() => setShowReasonForm(true)}
        onReasonChange={setReason}
        onCancelReason={clearReasonForm}
        onSubmitReason={submitReason}
      />
      {isResolved && (
        <EditShopRequestModalTranscriptRequirementResolution
          requirement={requirement}
          activeAction={activeAction}
          isSubmitting={isSubmitting}
          onClear={() => applyAction(TRANSCRIPT_REQUIREMENT_ACTION.CLEAR)}
        />
      )}
    </Container>
  );
};

export default EditShopRequestModalTranscriptRequired;
